/* HPN STACK-RECHNER
   Rechenkern portiert aus der geprüften Referenz der HPN-Produktdatenbank
   (rechner-paket/referenz/rechner.mjs, Stand 2026-08-07). Die Regeln stehen
   in STACK-RECHNER.md, Abschnitt 6. Der Kern rechnet, die Oberfläche weiter
   unten zeigt nur an. Keine Preise, keine Links, kein Warenkorb.

   Datenquelle: assets/hpn-stack-daten.json. Die Datei wird nie von Hand
   editiert, sie kommt aus der Produktdatenbank (STACK-RECHNER.md, Punkt 5). */
(function () {
  'use strict';

  /* ===== Rechenkern, Regeln 1 bis 7 ===== */

  /* Anzeigerundung: drei signifikante Stellen. In der Quelle stehen teils
     heruntergerechnete Pro-Kapsel-Werte, 116,67 mal 6 ergibt 700,02 statt
     der 700 auf der Dose. */
  function runde(x) {
    if (x === null || x === undefined || x === 0) return x;
    return Number(x.toPrecision(3));
  }

  function faktorFuer(einheit) {
    const f = { g: 1000, mg: 1, mcg: 0.001 }[einheit];
    if (f === undefined) throw new Error('Unbekannte Einheit ' + einheit);
    return f;
  }

  /* Regel 6: Welche Schwelle erreicht dieser Wert? Die höhere gewinnt.
     Gibt null zurück, wenn keine greift oder der Stoff keine Schwellen hat. */
  function stufeFuer(wirkstoff, wert) {
    if (!wirkstoff.schwellen) return null;
    let treffer = null;
    for (const s of wirkstoff.schwellen) {
      if (wert >= s.wert) {
        if (!treffer || s.wert > treffer.wert) treffer = s;
      }
    }
    return treffer;
  }

  /* Rechnet einen Stack aus. stack = [{ id, faktor }], faktor 1 = Tagesportion.
     Regel 1: nur rolle "bezugswert" geht in die Summe.
     Regel 2: summiert wird über das exakte Feld mg, nie über das gerundete wert.
     Regel 3: jede Menge wird mit dem Faktor des Produkts multipliziert.
     Regel 5: Zeilen ohne Masse werden gesammelt, aber nie addiert. */
  function rechneStack(daten, stack) {
    const produkte = new Map(daten.produkte.map(function (p) { return [p.id, p]; }));
    const summen = new Map();
    const ohneMasse = [];

    for (const eintrag of stack) {
      const produkt = produkte.get(eintrag.id);
      const faktor = eintrag.faktor === undefined ? 1 : eintrag.faktor;
      if (!produkt) throw new Error('Produkt ' + eintrag.id + ' steht nicht im Datensatz');

      for (const zeile of produkt.zeilen) {
        if (zeile.rolle !== 'bezugswert') continue;

        if (zeile.mg === null || zeile.mg === undefined) {
          ohneMasse.push({ produkt: produkt.name, zeile: zeile, faktor: faktor });
          continue;
        }

        const bisher = summen.get(zeile.slug) || { mg: 0, posten: [] };
        bisher.mg += zeile.mg * faktor;
        bisher.posten.push({
          produkt: produkt.name,
          produktId: produkt.id,
          mg: zeile.mg * faktor,
          form: zeile.form,
          etikett: zeile.etikett
        });
        summen.set(zeile.slug, bisher);
      }
    }

    const ergebnis = [];
    summen.forEach(function (eintrag, slug) {
      const w = daten.wirkstoffe[slug];
      if (!w) throw new Error('Wirkstoff ' + slug + ' fehlt im Datensatz');
      const wert = eintrag.mg / faktorFuer(w.einheit);
      ergebnis.push({
        slug: slug,
        name: w.name,
        kategorie: w.kategorie,
        mg: eintrag.mg,
        wert: runde(wert),
        einheit: w.einheit,
        ie: w.ie_je_einheit ? runde(wert * w.ie_je_einheit) : null,
        stufe: stufeFuer(w, wert),
        posten: eintrag.posten
      });
    });

    ergebnis.sort(function (a, b) { return b.mg - a.mg; });
    return { wirkstoffe: ergebnis, ohneMasse: ohneMasse };
  }

  /* Regel 4: Zutatenzeilen eines Produkts in Baumform, Unterzeilen unter
     ihrer Elternzeile, wie das "davon" auf dem Etikett. */
  function baum(produkt) {
    const oben = [];
    const kinder = new Map();
    for (const z of produkt.zeilen) {
      if (z.parent) {
        const liste = kinder.get(z.parent) || [];
        liste.push(z);
        kinder.set(z.parent, liste);
      } else {
        oben.push(z);
      }
    }
    return oben.map(function (z) {
      return Object.assign({}, z, { kinder: kinder.get(z.slug) || [] });
    });
  }

  /* Bedienung: die fünf Gruppen aus STACK-RECHNER.md, Abschnitt 3.
     Beschluss Sami 2026-08-07. Die Zuordnung wird aus den Daten hergeleitet,
     nicht als Produktliste gepflegt. Kein Kunde bekommt eine krumme
     Kapselmenge angeboten. Der Faktor ist immer Einheiten geteilt durch
     Einheiten je Tagesportion. */
  function bedienung(produkt) {
    const p = produkt.portion;

    if (p.optionen) {
      let standard = 0;
      for (let i = 0; i < p.optionen.length; i++) {
        if (p.optionen[i].standard) standard = i;
      }
      return { art: 'auswahl', optionen: p.optionen, standardIndex: standard };
    }

    if (p.modus === 'einheit') {
      return {
        art: 'zaehler',
        min: Math.round(p.min),
        max: Math.round(p.max),
        start: Math.round(p.einheiten),
        proPortion: p.einheiten,
        typ: p.typ
      };
    }

    /* Härtung gegen einen fehlerhaften Datenexport: ohne positive
       Schrittweite würde die Schleife nie enden. Dann gibt es nur die
       volle Portion, den sichersten Wert. */
    if (!(p.schritt > 0)) {
      return { art: 'segmente', faktoren: [p.max], proPortion: p.einheiten, typ: p.typ, standard: p.max };
    }
    const faktoren = [];
    for (let f = p.min; f <= p.max + 1e-9; f += p.schritt) {
      faktoren.push(Number(f.toFixed(4)));
    }
    const alleGanz = faktoren.every(function (f) {
      const k = f * p.einheiten;
      return Math.abs(k - Math.round(k)) < 1e-9;
    });

    if (p.typ === 'pulver' || alleGanz) {
      return { art: 'segmente', faktoren: faktoren, proPortion: p.einheiten, typ: p.typ, standard: 1 };
    }

    const maxEinheiten = Math.round(p.max * p.einheiten);
    if (maxEinheiten <= 1) {
      return { art: 'fix', proPortion: p.einheiten, typ: p.typ, faktor: p.max };
    }

    return {
      art: 'kapselzaehler',
      min: 1,
      max: maxEinheiten,
      start: Math.round(p.einheiten),
      proPortion: p.einheiten,
      typ: p.typ
    };
  }

  /* Deutsche Anzeige: Komma als Dezimaltrenner, Tausenderpunkt.
     Erst runden (drei signifikante Stellen), dann formatieren. */
  function formatiereZahl(x) {
    if (x === null || x === undefined) return '';
    const g = runde(x);
    return g.toLocaleString('de-DE', { maximumFractionDigits: 6 });
  }

  function formatiereIE(x) {
    if (x === null || x === undefined) return '';
    return Math.round(x).toLocaleString('de-DE');
  }

  const Kern = {
    runde: runde,
    faktorFuer: faktorFuer,
    stufeFuer: stufeFuer,
    rechneStack: rechneStack,
    baum: baum,
    bedienung: bedienung,
    formatiereZahl: formatiereZahl,
    formatiereIE: formatiereIE
  };

  /* Für die Testläufe in Node (.context/stack-rechner-tests/). Im Browser
     existiert module nicht, dann geht es unten mit der Oberfläche weiter. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Kern;
    return;
  }

  /* ===== Oberfläche ===== */

  /* Gruppen der Tagessumme, kuratierte Reihenfolge: erst die Kategorien mit
     Schwellen, dann der Rest. Schlüssel wie im Datensatz, mit echten Umlauten. */
  const KATEGORIEN = [
    ['vitamin', 'Vitamine'],
    ['mineralstoff', 'Mineralstoffe'],
    ['spurenelement', 'Spurenelemente'],
    ['aminosäure', 'Aminosäuren'],
    ['fettsäure', 'Fettsäuren'],
    ['protein', 'Proteine'],
    ['enzym', 'Enzyme'],
    ['ballaststoff', 'Ballaststoffe'],
    ['pilz', 'Pilze'],
    ['pflanzenextrakt', 'Pflanzenextrakte'],
    ['pflanzenstoff', 'Pflanzenstoffe'],
    ['sonstiger_stoff', 'Weitere Stoffe']
  ];

  const EINHEIT_NAMEN = {
    kapsel: ['Kapsel', 'Kapseln'],
    softgel: ['Softgel', 'Softgels'],
    pulver: ['Portion', 'Portionen']
  };

  const SPEICHER_SCHLUESSEL = 'hpn-stack-rechner-v1';

  function el(tag, klasse, text) {
    const e = document.createElement(tag);
    if (klasse) e.className = klasse;
    if (text !== undefined && text !== null) e.textContent = text;
    return e;
  }

  function einheitName(typ, anzahl) {
    const namen = EINHEIT_NAMEN[typ] || EINHEIT_NAMEN.kapsel;
    return anzahl === 1 ? namen[0] : namen[1];
  }

  /* Menge einer Zutatenzeile in ihrer Anzeige-Einheit, skaliert mit dem
     Faktor. Zeilen ohne Masse (FU) skalieren über das Anzeigefeld wert. */
  function zeilenMenge(zeile, faktor) {
    if (zeile.mg === null || zeile.mg === undefined) {
      return formatiereZahl(zeile.wert * faktor) + ' ' + zeile.einheit;
    }
    const wert = (zeile.mg * faktor) / faktorFuer(zeile.einheit);
    return formatiereZahl(wert) + ' ' + zeile.einheit;
  }

  /* Versionsstände gehören zur Datenpflege, nicht zum sichtbaren Namen.
     Andere Zahlen wie bei Omega 3 bleiben deshalb erhalten. */
  function produktAnzeigename(name) {
    return String(name || '').replace(/(?:\s+v\d+(?:\.\d+)*)+$/i, '').trim();
  }

  function init(wurzel) {
    if (wurzel.dataset.hsInit) return;
    wurzel.dataset.hsInit = '1';

    const greife = function (name) { return wurzel.querySelector('[data-hs-' + name + ']'); };
    const laden = greife('laden');
    const app = greife('app');
    const suchfeld = greife('suche');
    const trefferListe = greife('treffer');
    const keinTreffer = greife('keintreffer');
    const kartenBox = greife('karten');
    const stackLeer = greife('leer');
    const leerenKnopf = greife('leeren');
    const statusZeile = greife('status');
    const summenBox = greife('summen');
    const summenBlock = greife('summenblock');
    const mobilbar = greife('mobilbar');
    const mobilbarText = greife('mobilbar-text');

    /* Zustand: Map Produkt-ID auf Einstellung. faktor nur bei Segmenten,
       kapseln nur bei Zählern, optionIndex nur bei Auswahl. */
    const stack = new Map();
    const offeneZutaten = new Set();
    const offeneWirkstoffe = new Set();
    let daten = null;
    let suchindex = [];
    let summenSichtbar = false;

    fetch(wurzel.dataset.datenUrl)
      .then(function (antwort) {
        if (!antwort.ok) throw new Error('HTTP ' + antwort.status);
        return antwort.json();
      })
      .then(function (geladen) {
        daten = geladen;
        suchindex = daten.produkte.map(function (p) {
          const woerter = [p.name, p.formel || ''];
          p.zeilen.forEach(function (z) { woerter.push(z.name, z.etikett || ''); });
          return { produkt: p, text: woerter.join(' ').toLowerCase() };
        });
        /* Ein kaputter Speicherstand darf den Rechner nie lahmlegen:
           im Zweifel leer starten und den Schlüssel entsorgen. */
        try {
          ladeGespeichertenStack();
        } catch (fehler) {
          stack.clear();
          try { window.localStorage.removeItem(SPEICHER_SCHLUESSEL); } catch (egal) { }
        }
        laden.hidden = true;
        app.hidden = false;
        renderTreffer();
        renderStack();
        renderSummen();
      })
      .catch(function () {
        laden.hidden = true;
        laden.insertAdjacentElement('afterend', baueFehlermeldung());
      });

    function baueFehlermeldung() {
      const box = el('div', 'hs-meldung hs-fehler');
      box.appendChild(el('p', 'text-base', 'Der Rechner konnte nicht geladen werden.'));
      const knopf = el('button', 'hs-segment text-sm font-medium', 'Seite neu laden');
      knopf.type = 'button';
      knopf.addEventListener('click', function () { window.location.reload(); });
      box.appendChild(knopf);
      return box;
    }

    /* ===== Zustand und Rechnen ===== */

    function faktorVon(produkt) {
      const z = stack.get(produkt.id);
      const bed = bedienung(produkt);
      if (bed.art === 'auswahl') return bed.optionen[z.optionIndex].faktor;
      if (bed.art === 'zaehler' || bed.art === 'kapselzaehler') return z.kapseln / bed.proPortion;
      if (bed.art === 'fix') return bed.faktor;
      return z.faktor;
    }

    function startZustand(produkt) {
      const bed = bedienung(produkt);
      if (bed.art === 'auswahl') return { optionIndex: bed.standardIndex };
      if (bed.art === 'zaehler' || bed.art === 'kapselzaehler') return { kapseln: bed.start };
      if (bed.art === 'fix') return {};
      return { faktor: bed.standard };
    }

    function aktuellerStack() {
      const liste = [];
      stack.forEach(function (zustand, id) {
        const produkt = daten.produkte.find(function (p) { return p.id === id; });
        liste.push({ id: id, faktor: faktorVon(produkt) });
      });
      return liste;
    }

    function speichereStack() {
      try {
        const roh = [];
        stack.forEach(function (zustand, id) { roh.push(Object.assign({ id: id }, zustand)); });
        window.localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(roh));
      } catch (fehler) { /* privater Modus ohne Speicher ist in Ordnung */ }
    }

    function ladeGespeichertenStack() {
      let roh = null;
      try { roh = JSON.parse(window.localStorage.getItem(SPEICHER_SCHLUESSEL)); } catch (fehler) { return; }
      if (!Array.isArray(roh)) return;
      roh.forEach(function (eintrag) {
        if (!eintrag || typeof eintrag !== 'object') return;
        const produkt = daten.produkte.find(function (p) { return p.id === eintrag.id; });
        if (!produkt) return;
        const bed = bedienung(produkt);
        const zustand = startZustand(produkt);
        if ((bed.art === 'zaehler' || bed.art === 'kapselzaehler') && typeof eintrag.kapseln === 'number') {
          zustand.kapseln = Math.min(bed.max, Math.max(bed.min, Math.round(eintrag.kapseln)));
        }
        if (bed.art === 'auswahl' && typeof eintrag.optionIndex === 'number' && bed.optionen[eintrag.optionIndex]) {
          zustand.optionIndex = eintrag.optionIndex;
        }
        if (bed.art === 'segmente' && bed.faktoren.indexOf(eintrag.faktor) !== -1) {
          zustand.faktor = eintrag.faktor;
        }
        stack.set(produkt.id, zustand);
      });
    }

    function nachAenderung() {
      speichereStack();
      renderSummen();
    }

    /* ===== Suche und Trefferliste ===== */

    function renderTreffer() {
      const anfrage = suchfeld.value.trim().toLowerCase();
      const woerter = anfrage.split(/\s+/).filter(Boolean);
      const treffer = suchindex.filter(function (eintrag) {
        return woerter.every(function (w) { return eintrag.text.indexOf(w) !== -1; });
      });
      treffer.sort(function (a, b) { return a.produkt.name.localeCompare(b.produkt.name, 'de'); });

      trefferListe.textContent = '';
      treffer.forEach(function (eintrag) {
        const p = eintrag.produkt;
        const li = document.createElement('li');
        const knopf = el('button', 'hs-produktwahl text-sm');
        knopf.type = 'button';
        knopf.dataset.hsWahl = p.id;
        knopf.setAttribute('aria-pressed', stack.has(p.id) ? 'true' : 'false');
        const anzeigename = produktAnzeigename(p.name);
        knopf.setAttribute('aria-label', anzeigename + (stack.has(p.id) ? ' aus dem Stack entfernen' : ' zum Stack hinzufügen'));
        const name = el('span', 'hs-wahlname', anzeigename);
        knopf.appendChild(name);
        const status = el('span', 'hs-wahlstatus');
        status.setAttribute('aria-hidden', 'true');
        status.textContent = stack.has(p.id) ? '✓' : '+';
        knopf.appendChild(status);
        li.appendChild(knopf);
        trefferListe.appendChild(li);
      });
      trefferListe.hidden = treffer.length === 0;
      keinTreffer.hidden = treffer.length > 0;
    }

    /* ===== Stack-Karten ===== */

    function baueSteller(produkt) {
      const bed = bedienung(produkt);
      const zustand = stack.get(produkt.id);
      const box = el('div', 'hs-steller');

      if (bed.art === 'segmente') {
        bed.faktoren.forEach(function (f) {
          const knopf = el('button', 'hs-segment text-sm');
          knopf.type = 'button';
          knopf.dataset.hsSegment = String(f);
          knopf.setAttribute('aria-pressed', zustand.faktor === f ? 'true' : 'false');
          let beschriftung = f === 0.5 ? 'Halbe Portion' : f === 1 ? '1 Portion' : formatiereZahl(f) + ' Portionen';
          knopf.textContent = beschriftung;
          if (bed.typ !== 'pulver') {
            knopf.appendChild(document.createTextNode(' '));
            knopf.appendChild(el('span', 'hs-segmentinfo text-xs', '= ' + formatiereZahl(f * bed.proPortion) + ' ' + einheitName(bed.typ, f * bed.proPortion)));
          }
          box.appendChild(knopf);
        });
        return box;
      }

      if (bed.art === 'zaehler' || bed.art === 'kapselzaehler') {
        const gruppe = el('div', 'hs-zaehler');
        const minus = el('button', 'hs-zaehlerknopf', '−');
        minus.type = 'button';
        minus.dataset.hsSchritt = '-1';
        minus.setAttribute('aria-label', 'Eine ' + einheitName(bed.typ, 1) + ' weniger');
        const wert = el('span', 'hs-zaehlerwert text-sm font-medium');
        wert.dataset.hsZaehlerwert = '1';
        const plus = el('button', 'hs-zaehlerknopf', '+');
        plus.type = 'button';
        plus.dataset.hsSchritt = '1';
        plus.setAttribute('aria-label', 'Eine ' + einheitName(bed.typ, 1) + ' mehr');
        gruppe.appendChild(minus);
        gruppe.appendChild(wert);
        gruppe.appendChild(plus);
        box.appendChild(gruppe);
        return box;
      }

      if (bed.art === 'auswahl') {
        const feld = el('select', 'hs-auswahl text-sm');
        feld.setAttribute('aria-label', 'Einnahme von ' + produktAnzeigename(produkt.name));
        bed.optionen.forEach(function (option, i) {
          const o = document.createElement('option');
          o.value = String(i);
          o.textContent = option.text;
          if (i === zustand.optionIndex) o.selected = true;
          feld.appendChild(o);
        });
        box.appendChild(feld);
        return box;
      }

      box.appendChild(el('span', 'hs-fixmenge text-sm', '1 ' + einheitName(bed.typ, 1) + ' pro Tag'));
      return box;
    }

    function baueZutatenliste(produkt) {
      const faktor = faktorVon(produkt);
      const liste = el('ul', 'hs-zutatenliste text-xs');
      baum(produkt).forEach(function (zeile) {
        const li = document.createElement('li');
        li.appendChild(el('span', 'hs-zutatname', zeile.etikett || zeile.name));
        li.appendChild(el('span', 'hs-zutatwert', zeilenMenge(zeile, faktor)));
        liste.appendChild(li);
        zeile.kinder.forEach(function (kind) {
          const kli = document.createElement('li');
          kli.className = 'hs-eingerueckt';
          kli.appendChild(el('span', 'hs-zutatname', kind.etikett || kind.name));
          kli.appendChild(el('span', 'hs-zutatwert', zeilenMenge(kind, faktor)));
          liste.appendChild(kli);
        });
      });
      return liste;
    }

    function aktualisiereKarte(karte, produkt) {
      const bed = bedienung(produkt);
      const zustand = stack.get(produkt.id);
      if (bed.art === 'zaehler' || bed.art === 'kapselzaehler') {
        const wert = karte.querySelector('[data-hs-zaehlerwert]');
        const beschriftung = bed.art === 'kapselzaehler'
          ? zustand.kapseln + ' von ' + bed.max + ' ' + einheitName(bed.typ, bed.max)
          : zustand.kapseln + ' ' + einheitName(bed.typ, zustand.kapseln);
        wert.textContent = beschriftung;
        karte.querySelector('[data-hs-schritt="-1"]').disabled = zustand.kapseln <= bed.min;
        karte.querySelector('[data-hs-schritt="1"]').disabled = zustand.kapseln >= bed.max;
      }
      if (bed.art === 'segmente') {
        karte.querySelectorAll('[data-hs-segment]').forEach(function (knopf) {
          knopf.setAttribute('aria-pressed', Number(knopf.dataset.hsSegment) === zustand.faktor ? 'true' : 'false');
        });
      }
      const zutaten = karte.querySelector('[data-hs-zutatenbox]');
      zutaten.textContent = '';
      zutaten.appendChild(baueZutatenliste(produkt));
    }

    function renderStack() {
      kartenBox.textContent = '';
      stack.forEach(function (zustand, id) {
        const produkt = daten.produkte.find(function (p) { return p.id === id; });
        const karte = el('article', 'hs-karte');
        karte.dataset.hsKarte = id;

        const kopf = el('div', 'hs-kartenkopf');
        const name = el('h3', 'hs-kartenname heading text-lg-2xl leading-tight tracking-tight', produktAnzeigename(produkt.name));
        kopf.appendChild(name);
        const entfernen = el('button', 'hs-entfernen');
        entfernen.type = 'button';
        entfernen.dataset.hsEntfernen = id;
        entfernen.setAttribute('aria-label', produktAnzeigename(produkt.name) + ' entfernen');
        entfernen.appendChild(el('span', 'hs-entfernen-x', '✕'));
        kopf.appendChild(entfernen);
        karte.appendChild(kopf);

        karte.appendChild(baueSteller(produkt));

        if (produkt.portion.text) {
          karte.appendChild(el('p', 'hs-portionstext text-xs', produkt.portion.text));
        }

        const zutaten = el('details', 'hs-zutaten');
        zutaten.dataset.hsZutaten = id;
        if (offeneZutaten.has(id)) zutaten.open = true;
        const griff = el('summary', 'text-xs font-medium', 'Zutaten ansehen');
        zutaten.appendChild(griff);
        const boxInnen = el('div');
        boxInnen.dataset.hsZutatenbox = '1';
        zutaten.appendChild(boxInnen);
        karte.appendChild(zutaten);

        kartenBox.appendChild(karte);
        aktualisiereKarte(karte, produkt);
      });

      stackLeer.hidden = stack.size > 0;
      leerenKnopf.hidden = stack.size === 0;
    }

    /* ===== Tagessumme ===== */

    function renderSummen() {
      const ergebnis = stack.size > 0 ? rechneStack(daten, aktuellerStack()) : { wirkstoffe: [], ohneMasse: [] };

      summenBox.textContent = '';

      if (stack.size === 0) {
        statusZeile.textContent = '';
        summenBox.appendChild(el('p', 'hs-leer text-sm', 'Hier erscheinen alle Wirkstoffe deines Stacks als Tagessumme.'));
        aktualisiereMobilbar(0, 0, null);
        return;
      }

      /* Zeilen ohne Masse als normale Zeilen in ihrer Kategorie zeigen */
      const zeilen = ergebnis.wirkstoffe.slice();
      ergebnis.ohneMasse.forEach(function (eintrag) {
        const w = daten.wirkstoffe[eintrag.zeile.slug];
        zeilen.push({
          slug: eintrag.zeile.slug,
          name: w ? w.name : eintrag.zeile.name,
          kategorie: w ? w.kategorie : 'sonstiger_stoff',
          ohneMasse: true,
          anzeige: formatiereZahl(eintrag.zeile.wert * eintrag.faktor) + ' ' + eintrag.zeile.einheit,
          posten: [{ produkt: eintrag.produkt, anzeige: formatiereZahl(eintrag.zeile.wert * eintrag.faktor) + ' ' + eintrag.zeile.einheit }],
          stufe: null
        });
      });

      const gruppen = new Map();
      zeilen.forEach(function (zeile) {
        const liste = gruppen.get(zeile.kategorie) || [];
        liste.push(zeile);
        gruppen.set(zeile.kategorie, liste);
      });

      let hinweise = 0;
      let hoechsteStufe = null;

      const reihenfolge = KATEGORIEN.map(function (paar) { return paar[0]; });
      const alleSchluessel = reihenfolge.filter(function (k) { return gruppen.has(k); });
      gruppen.forEach(function (liste, schluessel) {
        if (reihenfolge.indexOf(schluessel) === -1) alleSchluessel.push(schluessel);
      });

      alleSchluessel.forEach(function (schluessel) {
        const paar = KATEGORIEN.find(function (p) { return p[0] === schluessel; });
        const gruppe = el('section', 'hs-gruppe');
        gruppe.appendChild(el('h3', 'hs-gruppentitel heading text-base leading-tight tracking-tight', paar ? paar[1] : 'Weitere Stoffe'));
        const liste = el('ul', 'hs-wirkstoffe');

        gruppen.get(schluessel).forEach(function (zeile) {
          const li = document.createElement('li');
          const details = el('details', 'hs-wz');
          details.dataset.hsWz = zeile.slug;
          if (offeneWirkstoffe.has(zeile.slug)) details.open = true;

          const kopf = el('summary');
          const name = el('span', 'hs-wzname text-sm', zeile.name);
          if (zeile.stufe) {
            hinweise++;
            if (zeile.stufe.stufe === 'orange' || hoechsteStufe === null) hoechsteStufe = zeile.stufe.stufe === 'orange' ? 'orange' : (hoechsteStufe || 'gelb');
            name.appendChild(document.createTextNode(' '));
            name.appendChild(el('span', 'hs-badge text-xs font-medium hs-badge--' + zeile.stufe.stufe, 'Hinweis'));
          }
          kopf.appendChild(name);

          const wert = el('span', 'hs-wzwert text-sm font-medium');
          if (zeile.ohneMasse) {
            wert.textContent = zeile.anzeige;
          } else {
            wert.textContent = formatiereZahl(zeile.wert) + ' ' + zeile.einheit;
            if (zeile.ie) wert.appendChild(el('span', 'hs-wzie text-xs', '(' + formatiereIE(zeile.ie) + ' IE)'));
          }
          kopf.appendChild(wert);
          details.appendChild(kopf);

          const posten = el('ul', 'hs-posten text-xs');
          zeile.posten.forEach(function (p) {
            const pli = document.createElement('li');
            pli.appendChild(el('span', 'hs-postenname', produktAnzeigename(p.produkt)));
            const anzeige = zeile.ohneMasse
              ? p.anzeige
              : formatiereZahl(p.mg / faktorFuer(zeile.einheit)) + ' ' + zeile.einheit;
            pli.appendChild(el('span', 'hs-postenwert', anzeige));
            posten.appendChild(pli);
          });
          details.appendChild(posten);
          li.appendChild(details);

          if (zeile.stufe) {
            li.appendChild(el('p', 'hs-schwellenhinweis text-xs hs-schwellenhinweis--' + zeile.stufe.stufe, zeile.stufe.hinweis));
          }
          liste.appendChild(li);
        });

        gruppe.appendChild(liste);
        summenBox.appendChild(gruppe);
      });

      const produkteText = stack.size === 1 ? '1 Produkt' : stack.size + ' Produkte';
      const wirkstoffText = zeilen.length === 1 ? '1 Wirkstoff' : zeilen.length + ' Wirkstoffe';
      const hinweisText = hinweise === 0 ? '' : ', ' + (hinweise === 1 ? '1 Hinweis' : hinweise + ' Hinweise');
      statusZeile.textContent = produkteText + ', ' + wirkstoffText + hinweisText;

      aktualisiereMobilbar(stack.size, hinweise, hoechsteStufe);
    }

    /* ===== Mobile Leiste ===== */

    function aktualisiereMobilbar(produkte, hinweise, stufe) {
      mobilbarText.textContent = produkte === 1 ? '1 Produkt' : produkte + ' Produkte';
      const alt = mobilbar.querySelector('.hs-mobilbar-hinweise');
      if (alt) alt.remove();
      if (hinweise > 0) {
        const span = el('span', 'hs-mobilbar-hinweise text-sm' + (stufe === 'orange' ? ' hs-mobilbar-hinweise--orange' : ''),
          hinweise === 1 ? '1 Hinweis' : hinweise + ' Hinweise');
        mobilbarText.insertAdjacentElement('afterend', span);
      }
      zeigeOderVersteckeMobilbar();
    }

    /* Das Theme hat mobil eine eigene fixe Fußleiste (nav.mobile-dock).
       Die Sprungleiste muss darüber schweben, sonst verdecken sich beide.
       Das Dock wird direkt gemessen, denn es blendet sich beim Scrollen
       über eine invisible-Klasse aus und wieder ein: die Höhe bleibt dabei
       messbar, so steht die Sprungleiste ruhig statt zu springen. Der
       Fallback über elementFromPoint greift, falls das Theme die Leiste
       einmal anders baut. */
    function messeDockHoehe() {
      let hoehe = 0;
      const dock = document.querySelector('.mobile-dock');
      if (dock) {
        hoehe = Math.round(dock.getBoundingClientRect().height);
      } else {
        const vorher = mobilbar.hidden;
        mobilbar.hidden = true;
        let knoten = document.elementFromPoint(Math.floor(window.innerWidth / 2), window.innerHeight - 4);
        while (knoten && knoten !== document.body && knoten !== document.documentElement) {
          if (window.getComputedStyle(knoten).position === 'fixed') {
            hoehe = Math.round(window.innerHeight - knoten.getBoundingClientRect().top);
            break;
          }
          knoten = knoten.parentElement;
        }
        mobilbar.hidden = vorher;
      }
      /* Über 140px ist es kein Dock mehr, sondern ein offenes Overlay */
      if (hoehe > 0 && hoehe <= 140) {
        wurzel.style.setProperty('--hs-dock-hoehe', hoehe + 'px');
      } else {
        wurzel.style.removeProperty('--hs-dock-hoehe');
      }
    }

    function zeigeOderVersteckeMobilbar() {
      const schmal = !window.matchMedia('(min-width: 990px)').matches;
      messeDockHoehe();
      mobilbar.hidden = !(schmal && stack.size > 0 && !summenSichtbar);
    }

    /* Aufräumliste für den Theme-Editor: shopify:section:load ersetzt das
       Section-DOM, ohne Aufräumen sammeln sich window-Listener und
       Observer verwaister Instanzen an. Im Storefront feuert das nie. */
    const aufraeumer = [];

    if ('IntersectionObserver' in window) {
      const beobachter = new IntersectionObserver(function (eintraege) {
        summenSichtbar = eintraege[0].isIntersecting;
        zeigeOderVersteckeMobilbar();
      }, { rootMargin: '0px 0px -40px 0px' });
      beobachter.observe(summenBlock);
      aufraeumer.push(function () { beobachter.disconnect(); });
    } else {
      summenSichtbar = true;
    }
    window.addEventListener('resize', zeigeOderVersteckeMobilbar);
    aufraeumer.push(function () { window.removeEventListener('resize', zeigeOderVersteckeMobilbar); });
    let scrollGeplant = false;
    const beiScroll = function () {
      if (scrollGeplant) return;
      scrollGeplant = true;
      window.requestAnimationFrame(function () {
        scrollGeplant = false;
        zeigeOderVersteckeMobilbar();
      });
    };
    window.addEventListener('scroll', beiScroll, { passive: true });
    aufraeumer.push(function () { window.removeEventListener('scroll', beiScroll); });

    document.addEventListener('shopify:section:unload', function beiUnload() {
      if (wurzel.isConnected) return;
      aufraeumer.forEach(function (f) { f(); });
      document.removeEventListener('shopify:section:unload', beiUnload);
    });

    /* ===== Ereignisse ===== */

    suchfeld.addEventListener('input', renderTreffer);

    wurzel.addEventListener('click', function (ereignis) {
      const wahl = ereignis.target.closest('[data-hs-wahl]');
      if (wahl) {
        const id = wahl.dataset.hsWahl;
        if (stack.has(id)) {
          stack.delete(id);
          offeneZutaten.delete(id);
        } else {
          const produkt = daten.produkte.find(function (p) { return p.id === id; });
          stack.set(id, startZustand(produkt));
        }
        renderTreffer();
        const neueWahl = Array.from(trefferListe.querySelectorAll('[data-hs-wahl]')).find(function (knopf) {
          return knopf.dataset.hsWahl === id;
        });
        if (neueWahl) neueWahl.focus();
        renderStack();
        nachAenderung();
        return;
      }

      const entfernen = ereignis.target.closest('[data-hs-entfernen]');
      if (entfernen) {
        stack.delete(entfernen.dataset.hsEntfernen);
        offeneZutaten.delete(entfernen.dataset.hsEntfernen);
        renderTreffer();
        renderStack();
        nachAenderung();
        return;
      }

      if (ereignis.target.closest('[data-hs-leeren]')) {
        stack.clear();
        offeneZutaten.clear();
        renderTreffer();
        renderStack();
        nachAenderung();
        return;
      }

      const segment = ereignis.target.closest('[data-hs-segment]');
      if (segment) {
        const karte = segment.closest('[data-hs-karte]');
        const produkt = daten.produkte.find(function (p) { return p.id === karte.dataset.hsKarte; });
        stack.get(produkt.id).faktor = Number(segment.dataset.hsSegment);
        aktualisiereKarte(karte, produkt);
        nachAenderung();
        return;
      }

      const schritt = ereignis.target.closest('[data-hs-schritt]');
      if (schritt) {
        const karte = schritt.closest('[data-hs-karte]');
        const produkt = daten.produkte.find(function (p) { return p.id === karte.dataset.hsKarte; });
        const bed = bedienung(produkt);
        const zustand = stack.get(produkt.id);
        zustand.kapseln = Math.min(bed.max, Math.max(bed.min, zustand.kapseln + Number(schritt.dataset.hsSchritt)));
        aktualisiereKarte(karte, produkt);
        nachAenderung();
        return;
      }

      if (ereignis.target.closest('[data-hs-mobilbar]')) {
        summenBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    wurzel.addEventListener('change', function (ereignis) {
      const auswahl = ereignis.target.closest('.hs-auswahl');
      if (!auswahl) return;
      const karte = auswahl.closest('[data-hs-karte]');
      const produkt = daten.produkte.find(function (p) { return p.id === karte.dataset.hsKarte; });
      stack.get(produkt.id).optionIndex = Number(auswahl.value);
      aktualisiereKarte(karte, produkt);
      nachAenderung();
    });

    /* Aufklappzustände überleben das Neurechnen */
    wurzel.addEventListener('toggle', function (ereignis) {
      const zutaten = ereignis.target.closest('[data-hs-zutaten]');
      if (zutaten) {
        if (zutaten.open) offeneZutaten.add(zutaten.dataset.hsZutaten);
        else offeneZutaten.delete(zutaten.dataset.hsZutaten);
        return;
      }
      const wz = ereignis.target.closest('[data-hs-wz]');
      if (wz) {
        if (wz.open) offeneWirkstoffe.add(wz.dataset.hsWz);
        else offeneWirkstoffe.delete(wz.dataset.hsWz);
      }
    }, true);
  }

  function initAlle() {
    document.querySelectorAll('[data-hpnstack]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlle);
  } else {
    initAlle();
  }
  document.addEventListener('shopify:section:load', initAlle);
})();
